import React, { useState, useEffect } from 'react';
import { X, Download, Share, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

const FAVICON_URL = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/q9v3nz6d_favicon-riding-watt.png";

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check if user dismissed recently
    const dismissed = localStorage.getItem('rw_install_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed);
      // Show again after 3 days
      if (Date.now() - dismissedAt < 3 * 24 * 60 * 60 * 1000) return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Android/Desktop: listen for install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show banner after 5 seconds of browsing
      setTimeout(() => setShowBanner(true), 5000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS: show after 5 seconds
    if (iOS) {
      setTimeout(() => setShowBanner(true), 5000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
      localStorage.setItem('rw_install_dismissed', Date.now().toString());
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('rw_install_dismissed', Date.now().toString());
  };

  if (isStandalone || !showBanner) return null;

  // iOS instructions overlay
  if (showIOSGuide) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end justify-center p-4" data-testid="ios-install-guide">
        <div className="w-full max-w-md bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl p-6 mb-4 animate-slide-up space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold text-lg">Installer Riding Watt</h3>
            <button onClick={handleDismiss} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00FF00]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#00FF00] font-bold text-sm">1</span>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Appuyez sur le bouton Partager</p>
                <p className="text-gray-400 text-xs mt-0.5">Le carré avec la flèche vers le haut <Share className="w-3 h-3 inline" /> en bas de Safari</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00FF00]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#00FF00] font-bold text-sm">2</span>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Faites défiler et appuyez sur</p>
                <p className="text-gray-400 text-xs mt-0.5">"Sur l'écran d'accueil" (+)</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#00FF00]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-[#00FF00] font-bold text-sm">3</span>
              </div>
              <div>
                <p className="text-white text-sm font-medium">Appuyez sur "Ajouter"</p>
                <p className="text-gray-400 text-xs mt-0.5">L'app apparaîtra sur votre écran d'accueil</p>
              </div>
            </div>
          </div>

          <Button onClick={handleDismiss} className="w-full bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold py-5">
            Compris !
          </Button>
        </div>
      </div>
    );
  }

  // Install banner
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[90] p-4 animate-slide-up" data-testid="install-banner">
      <div className="max-w-md mx-auto bg-gradient-to-r from-[#0A0A0A] to-[#1A1A1A] border border-[#00FF00]/30 rounded-2xl p-4 shadow-[0_0_30px_rgba(0,255,0,0.15)]">
        <div className="flex items-center gap-3">
          <img src={FAVICON_URL} alt="" className="w-12 h-12 rounded-xl shadow-[0_0_15px_rgba(0,255,0,0.3)]" />
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">Installer Riding Watt</p>
            <p className="text-gray-400 text-xs truncate">Accès rapide + notifications promos</p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleInstall} size="sm" className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold px-4 shadow-[0_0_15px_rgba(0,255,0,0.3)]" data-testid="install-btn">
              {isIOS ? <Share className="w-4 h-4 mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              Installer
            </Button>
            <button onClick={handleDismiss} className="text-gray-500 hover:text-white p-1" data-testid="install-dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
